import useGetShipDataForMap from "@components/map/dataGettterHooks/useGetShipDataForMap"
import MapContainer from "@components/map/windyMap/MapContainer"
import SidePanelMapNL from "@components/sidePanelMap/SidePanelMapNL"
import { UserManual } from "@components/userManual"
import { fleetOverviewSteps } from "@components/userManual/moduleSteps/FleetOverviewManual"
import HomePageListOfOperationPortalPlants from "@pages/HomePage/operationPortalTable/HomePageListOfOperationPortalPlants"
import { NotFoundPageNL } from "@pages/NotFoundPage/NotFoundPage"
import { RequestStatus } from "@redux/app/RequestStatus"
import { useAppDispatch, useAppSelector } from "@redux/app/hooks"
import { useAppStructure } from "@redux/features/appStructure/useAppStructure"
import {
	getCurrentCompany,
	setCurrentCompany,
} from "@redux/features/currentCompany/currentCompanySlice"
import { getPanelState } from "@redux/features/mapSidePanelControl/mapSidePanelControlSlice"
import {
	clearSelectedVessel,
	getSelectedVessel,
} from "@redux/features/selectedVessel/selectedVesselSlice"
import { fetchUserData } from "@redux/features/userData/userDataSlice"
import { useGetWarningCondition, useGetWarningDialogHeight } from "@sop/DashboardPage/DashboardPage"
import { LoadingCircle } from "@ui-lib/src"
import { getPlantDataForVessel } from "@components/map/utils/vesselMetadata"
import { AppStructure } from "api"
import {
	ReactElement,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { useTranslation } from "react-i18next"
import { Step } from "react-joyride"
import EventLogContent from "./EventLogContent"
import KpisContent from "./KpisContent"
import VesselsContentNL from "./VesselsContentNL"

function ExpandedTable(): ReactElement {
	const { data: appStruct } = useAppStructure()
	return appStruct != null ? <HomePageListOfOperationPortalPlants props={appStruct} /> : <></>
}

function useFollowElementRect(
	target: () => HTMLElement | null,
	onRect: (r: DOMRect | null) => void
) {
	const raf = useRef<number | null>(null)

	useLayoutEffect(() => {
		const update = () => {
			if (raf.current !== null) cancelAnimationFrame(raf.current)
			raf.current = requestAnimationFrame(() => {
				const el = target()
				onRect(el !== null ? el.getBoundingClientRect() : null)
			})
		}

		update()

		const el = target()
		let ro: ResizeObserver | null = null

		if (el !== null && "ResizeObserver" in window) {
			ro = new ResizeObserver(update)
			ro.observe(el)
		}

		window.addEventListener("resize", update, { passive: true })
		window.addEventListener("scroll", update, { passive: true })

		return () => {
			if (raf.current !== null) cancelAnimationFrame(raf.current)
			window.removeEventListener("resize", update)
			window.removeEventListener("scroll", update)
			ro?.disconnect()
		}
	}, [target, onRect])
}

function moveAnchorToRect(anchorId: string, r: DOMRect | null) {
	const a = document.getElementById(anchorId)
	if (a === null) return

	if (r === null) {
		a.style.left = "-9999px"
		a.style.top = "-9999px"
		return
	}

	a.style.left = `${r.left}px`
	a.style.top = `${r.top}px`
	a.style.width = `${r.width}px`
	a.style.height = `${r.height}px`
}

function ComponentBody({ props, currentCompany }: { props: AppStructure; currentCompany: string }) {
	const dispatch = useAppDispatch()
	const [vessels, isPositionDataLoading, isLiveOpModeDataLoading] = useGetShipDataForMap(
		props,
		currentCompany,
		undefined,
		false
	)

	const [startTourFn, setStartTourFn] = useState<(() => void) | undefined>(undefined)

	const handleStartTour = useCallback((fn: () => void) => {
		setStartTourFn(() => fn)
	}, [])

	const selectedVessel = useAppSelector(getSelectedVessel)

	useEffect(() => {
		if (selectedVessel.name === "") {
			return
		}
		if (getPlantDataForVessel(selectedVessel, currentCompany) === undefined) {
			dispatch(clearSelectedVessel())
		}
	}, [currentCompany, dispatch, selectedVessel.name, selectedVessel.plantId])

	const mapRef = useRef<HTMLDivElement>(null)

	const contents: ReactElement[] = [
		<VesselsContentNL
			showHeader={currentCompany.toLowerCase() === "dipai" ? false : true}
			onUserManualClick={startTourFn}
		/>,
		<KpisContent />,
		<EventLogContent />,
	]

	const postStatus = useAppSelector((state) => state.userData.status)

	useEffect(() => {
		if (postStatus === RequestStatus.idle) {
			dispatch(fetchUserData())
		}
	}, [postStatus, dispatch])

	const { isOpen } = useAppSelector(getPanelState)

	const warningCondition = useGetWarningCondition(currentCompany)
	const warningDialogHeight = useGetWarningDialogHeight(currentCompany)

	const el = document.querySelector("#map-table")
	const tUserManual = useTranslation("userManual").t
	const steps: Step[] = useMemo(() => fleetOverviewSteps(tUserManual), [el])

	useFollowElementRect(
		() => document.querySelector<HTMLElement>("#fleet-details-arrow"),
		(r) => moveAnchorToRect("fleet-details-arrow-virtual", r)
	)
	return currentCompany === undefined ? (
		<NotFoundPageNL
			linkTo={"./../"}
			labelTo={"Go back"}
		/>
	) : isPositionDataLoading === true || isLiveOpModeDataLoading === true ? (
		<LoadingCircle containerHeightWithUnit="100vh" />
	) : (
		// : paramSelectedVessel !== "" &&
		//   Array.isArray(filteredVesselList) &&
		//   filteredVesselList.length === 0 ? (
		// 	<NotFoundPageNL linkTo={"./../"} labelTo={"Go to Map"} />
		// )
		<>
			<UserManual
				moduleName="fleetOverview"
				onStartTour={handleStartTour}
				stepsFromOutside={steps}
			/>
			<div
				className={`relative w-full overflow-hidden`}
				style={{
					height:
						warningCondition === true
							? `calc(100vh - ${warningDialogHeight}px)`
							: "100vh",
				}}
			>
				<div
					id="map-window"
					ref={mapRef}
					className={`absolute min-h-150 ${
						isOpen === false
							? "right-0 w-full duration-500"
							: "right-0 w-[calc(100%-590px)] duration-500"
					} transition-[width]`}
					style={{
						height:
							currentCompany.toLowerCase() === "dipai"
								? warningCondition === true
									? `calc(100vh - 52px - ${warningDialogHeight}px)`
									: `calc(100vh - 52px)`
								: warningCondition === true
									? `calc(100vh - ${warningDialogHeight}px)`
									: "100vh",
					}}
				>
					<MapContainer
						clientId={`${currentCompany}:All`}
						specificPlant={undefined}
						props={props}
					/>
				</div>
				<div
					id="fleet-details-arrow-virtual"
					className="pointer-events-none fixed z-999999 h-0 w-0"
				/>
				<SidePanelMapNL
					contents={contents}
					contentsLabels={["Operation", "KPIs", "Events Log"]}
					ExpandedTable={ExpandedTable}
				/>
			</div>
		</>
	)
}

export default function MapPageContent({ props }: { props: AppStructure }): ReactElement {
	const dispatch = useAppDispatch()
	const companyName = Object.keys(props)[0]
	const currentCompany = useAppSelector(getCurrentCompany) ?? ""
	if (currentCompany === "" || currentCompany !== companyName) {
		dispatch(setCurrentCompany(companyName))
	}
	if (currentCompany === undefined || companyName === undefined) {
		return (
			<NotFoundPageNL
				linkTo={"./../"}
				labelTo={"Go back"}
			/>
		)
	}
	return (
		<ComponentBody
			props={props}
			currentCompany={companyName}
		/>
	)
}
